// src/features/patient/care-treatment/components/WhatWeFound.tsx

/**
 * Purpose : "What we found" in an upload, on the upload result screen
 * (not on the Care & Treatment page, 2026-10-29). Counts and dates only; a
 * copy of a file already uploaded says nothing new was added.
 *
 * Contents :
 *
 * * function(uploadSummaryText) : "30 visits · 86 doctors’ notes · … · Apr 2016 – Jun 2017 · 3 departments"
 * * function(UploadCareSummary) : the upload result screen's summary (fetches its own)
 */

import { useQuery } from "@tanstack/react-query";
import { cn } from "@/shared/lib/utils";
import type { UploadSummary } from "@/features/patient/care-treatment/api/careTimelineTypes";
import { uploadCareSummaryQuery } from "@/portals/patient/api/patientQueries";
import { subTextClass } from "./careStyles";

export const COPY_MESSAGE = "This file matches one you already uploaded; nothing new was added.";

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const monthYear = (iso?: string) => {
  if (!iso || !/^\d{4}-\d{2}/.test(iso)) return "";
  return new Date(+iso.slice(0, 4), +iso.slice(5, 7) - 1, 1).toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

// function(uploadSummaryText)
/** The counts that aren't zero, the date range and the departments, joined by " · ". */
export function uploadSummaryText(summary: UploadSummary): string {
  const from = monthYear(summary.dateFrom);
  const to = monthYear(summary.dateTo);
  const range = from && to && from !== to ? `${from} – ${to}` : from || to;
  return [
    summary.visits > 0 && plural(summary.visits, "visit", "visits"),
    summary.doctorsNotes > 0 && plural(summary.doctorsNotes, "doctor’s note", "doctors’ notes"),
    summary.callsAndMessages > 0 && plural(summary.callsAndMessages, "call or message", "calls & messages"),
    range,
    summary.departments > 0 && plural(summary.departments, "department", "departments"),
  ].filter(Boolean).join(" · ");
}

/** The banner's look: a white card with the platform's pink accent on the left. */
const bannerClass = "rounded-2xl border border-[#E5E7EB] border-l-4 border-l-[#F36F8F] bg-white px-4 py-3 shadow-sm";

// function(UploadCareSummary)
/**
 * The upload result screen's summary. Renders
 * nothing until the server has one for this upload.
 */
export function UploadCareSummary({ uploadId, actingAsProfileId }: { uploadId: string; actingAsProfileId?: string }) {
  const { data } = useQuery(uploadCareSummaryQuery(actingAsProfileId, uploadId));
  if (!data) return null;
  const text = data.copyOfFileName ? COPY_MESSAGE : uploadSummaryText(data);
  if (!text) return null;
  return (
    <div role="status" data-testid="upload-what-we-found" className={cn("mt-3", bannerClass)}>
      <p className="!mb-0 text-sm font-bold text-[#0A2947]">What we found</p>
      <p className={cn("!mb-0", subTextClass)}>{text}</p>
    </div>
  );
}
